/** Statuses that stay on the MISC Outstanding queue. File Now is what publishes the report. */
export const ADD_NEW_DEFAULT_STATUS = "Not Started";

export const ADD_NEW_OUTSTANDING_STATUSES = [
  "Not Started",
  "Waiting on Client",
] as const;

export function isAddNewOutstandingStatus(
  status: string | null | undefined,
): boolean {
  const normalized = String(status ?? "").trim();
  return (ADD_NEW_OUTSTANDING_STATUSES as readonly string[]).includes(
    normalized,
  );
}

/** Calendar date from a schedule due date. Ignores the time so the day does not shift. */
export function dueDatePartsFromScheduleValue(
  value: unknown,
): { month: string; day: string; year: string } | null {
  const text = String(value ?? "").trim();
  if (!text) return null;

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return { year: iso[1], month: iso[2], day: iso[3] };
  }

  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return {
      month: us[1].padStart(2, "0"),
      day: us[2].padStart(2, "0"),
      year: us[3],
    };
  }

  return null;
}

/** MM/DD/YYYY for the form so the API does not shift the calendar day. */
export function dueDateForForm(value: unknown): string {
  if (value == null || String(value).trim() === "") return "";
  const parts = dueDatePartsFromScheduleValue(value);
  if (!parts) return String(value).trim();
  return `${parts.month}/${parts.day}/${parts.year}`;
}

/** A stored 0 on a new outstanding line means tax has not been entered yet. */
export function taxAmountForFileNowStart(value: unknown): string {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text) return "";
  const amount = Number(text.replace(/[$,\s]/g, ""));
  if (Number.isFinite(amount) && amount === 0) return "";
  return text;
}

/** Sort key from the calendar date in the schedule value. */
export function dueDateSortKey(value: unknown): number {
  const parts = dueDatePartsFromScheduleValue(value);
  if (!parts) return Number.MAX_SAFE_INTEGER;
  return (
    Number(parts.year) * 10000 + Number(parts.month) * 100 + Number(parts.day)
  );
}

const PERIOD_START_MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

/** Calendar order of the period itself. January before August, even when August is due sooner. */
export function periodCalendarSortKey(period: {
  period?: unknown;
  Period?: unknown;
  timePeriod?: unknown;
  TimePeriod?: unknown;
}): number {
  const periodNumber = Number(period.period ?? period.Period);
  if (Number.isFinite(periodNumber) && periodNumber > 0) return periodNumber;

  const label = String(period.timePeriod ?? period.TimePeriod ?? "")
    .trim()
    .toLowerCase();
  const monthIndex = PERIOD_START_MONTHS.findIndex((month) =>
    label.startsWith(month),
  );
  if (monthIndex >= 0) return monthIndex + 1;
  return Number.MAX_SAFE_INTEGER;
}
